import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CTAFooter } from "@/pages/Prestations";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import { useSeo, JsonLd } from "@/lib/seo";
import { publicCatalogApi } from "@/lib/api";
import {
  Check, ArrowRight, Mic, Video, Truck, Sparkles, PhoneCall,
  Heart, Cake, GlassWater, Building2, PartyPopper,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

// ── Fallback catalogue en cas d'échec API — jamais utilisé pour altérer les prix ─
// Les 3 forfaits existent en base (catégorie livre-or, ÉTAPE 1). Cette table est
// affichée UNIQUEMENT si /catalog/public répond en erreur, pour éviter d'afficher
// une page cassée. Elle NE crée rien en base.
const FALLBACK = {
  "Livre d'Or Audio": 129,
  "Livre d'Or Audio & Vidéo": 199,
  "Support Livre d'Or": 29,
};

// Vraies photos produit fournies par le client (hébergées localement)
const HERO_IMG = "/assets/livre-or/scene.jpg";
const AUDIO_IMG = "/assets/livre-or/audio.jpg";
const VIDEO_IMG = "/assets/livre-or/audio-video.jpg";
// Artwork illustré (fallback / Support) — icône Lucide XL sur fond dark/gold + grain.

function ProductArtwork({ variant = "audio", label, className = "" }) {
  const config = {
    audio: {
      Icon: PhoneCall,
      gradient: "from-[#1a1408] via-[#0a0a0a] to-[#080605]",
      accent: "bg-[#DCB457]/12",
    },
    video: {
      Icon: Video,
      gradient: "from-[#1a1408] via-[#0a0a0a] to-[#080605]",
      accent: "bg-[#DCB457]/16",
    },
    support: {
      Icon: Truck,
      gradient: "from-[#0f0d08] via-[#0a0a0a] to-[#080605]",
      accent: "bg-[#DCB457]/10",
    },
  }[variant];
  const { Icon, gradient, accent } = config;
  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${gradient} grain ${className}`}
    >
      {/* Spotlight gold */}
      <div className={`absolute -top-16 -right-16 w-72 h-72 ${accent} rounded-full blur-3xl`} />
      <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#DCB457]/8 rounded-full blur-3xl" />

      {/* Fine grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#DCB457 1px, transparent 1px), linear-gradient(90deg, #DCB457 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Icon center */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8">
        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-[#DCB457]/30 flex items-center justify-center bg-black/40 backdrop-blur-sm mb-5">
          <Icon
            size={56}
            strokeWidth={0.9}
            className="text-[#DCB457]"
            aria-hidden
          />
        </div>
        {label && (
          <span className="text-[10px] uppercase tracking-[0.32em] text-[#DCB457]/85 text-center">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

const LIVRE_OR_FAQS = [
  {
    q: "Qu'est-ce qu'un livre d'or audio ?",
    a: "Un livre d'or audio est un dispositif inspiré des téléphones vintage qui permet à vos invités d'enregistrer un message vocal en toute simplicité pendant votre événement. Un souvenir authentique, à réécouter longtemps après la soirée.",
  },
  {
    q: "Quelle est la différence entre le livre d'or audio et audio & vidéo ?",
    a: "Le livre d'or audio capture uniquement la voix de vos invités. La formule audio & vidéo ajoute la captation vidéo — vous conservez à la fois les mots, les expressions et l'émotion visible sur les visages.",
  },
  {
    q: "Peut-on ajouter le support installation/livraison ?",
    a: "Oui, l'option Support Livre d'Or (29 €) couvre la livraison, l'installation sur site et la reprise du matériel. Elle vient en complément de votre formule Audio ou Audio & Vidéo — pour profiter pleinement de votre événement sans logistique.",
  },
  {
    q: "Le Livre d'Or est-il adapté aux mariages ?",
    a: "Absolument. C'est l'un des usages les plus fréquents : chaque invité laisse un mot, une anecdote ou une déclaration à découvrir plus tard. Le Livre d'Or convient également aux anniversaires, soirées privées, événements familiaux ou d'entreprise.",
  },
  {
    q: "Combien coûte un Livre d'Or My Animation ?",
    a: "Livre d'Or Audio : 129 € TTC. Livre d'Or Audio & Vidéo : 199 € TTC. Support installation/livraison : 29 € TTC en option. Toutes les prestations sont personnalisables — nous établissons un devis clair sans engagement.",
  },
];

const EVENT_TYPES = [
  { icon: Heart, label: "Mariage" },
  { icon: Cake, label: "Anniversaire" },
  { icon: GlassWater, label: "Soirée privée" },
  { icon: Building2, label: "Événement d'entreprise" },
  { icon: PartyPopper, label: "Événement familial" },
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Vous choisissez votre formule",
    text: "Audio ou Audio & Vidéo, choisissez l'expérience qui correspond à votre événement.",
  },
  {
    n: "02",
    title: "Vos invités laissent leurs messages",
    text: "Ils enregistrent simplement leur message pendant la soirée. Pas besoin d'être technicien.",
  },
  {
    n: "03",
    title: "Vous conservez vos souvenirs",
    text: "Les messages deviennent de précieux souvenirs à réécouter après votre événement.",
  },
];

const AUDIO_POINTS = [
  "Messages vocaux illimités pendant la prestation",
  "Expérience simple et intuitive",
  "Souvenirs authentiques et spontanés",
  "Idéal pour mariages, anniversaires et événements privés",
];

const VIDEO_POINTS = [
  "Messages audio et vidéo",
  "Souvenirs encore plus immersifs",
  "Expérience premium pour les invités",
  "Idéal pour mariages et événements importants",
];

export default function LivreOr() {
  useSeo({
    title: "Livre d'Or Audio & Vidéo pour Mariage | My Animation",
    description:
      "Livre d'Or audio et vidéo pour mariage et événements. Faites enregistrer les messages, souvenirs et témoignages de vos invités avec My Animation.",
    path: "/prestations/livre-d-or",
    keywords:
      "livre d'or mariage, livre d'or audio, livre d'or vidéo, livre d'or Nord, livre d'or Hauts-de-France, messages invités mariage",
  });

  const [catalog, setCatalog] = useState({}); // { name: price_ttc }

  useEffect(() => {
    publicCatalogApi
      .byCategory("livre-or", true)
      .then((data) => {
        const map = {};
        (data.packages || []).forEach((p) => {
          map[p.name] = p.price_ttc;
        });
        setCatalog(map);
      })
      .catch(() => setCatalog({}));
  }, []);

  // Résolution du prix — API en priorité, sinon fallback statique cohérent avec la base
  const price = (name) => {
    const v = catalog[name] != null ? catalog[name] : FALLBACK[name];
    return `${Math.round(v)} €`;
  };

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Livre d'Or audio & vidéo",
    description:
      "Livre d'Or audio et vidéo pour mariage, anniversaire, soirée privée et événement d'entreprise. Vos invités enregistrent leurs messages, souvenirs et témoignages tout au long de votre événement.",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    offers: [
      {
        "@type": "Offer",
        name: "Livre d'Or Audio",
        price: "129",
        priceCurrency: "EUR",
      },
      {
        "@type": "Offer",
        name: "Livre d'Or Audio & Vidéo",
        price: "199",
        priceCurrency: "EUR",
      },
      {
        "@type": "Offer",
        name: "Support Livre d'Or (livraison + installation)",
        price: "29",
        priceCurrency: "EUR",
      },
    ],
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: LIVRE_OR_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="livre-or-page">
      <JsonLd data={serviceLd} id="ld-livre-or-service" />
      <JsonLd data={faqLd} id="ld-livre-or-faq" />

      {/* ═══ HERO CUSTOM (2 colonnes desktop, 1 colonne mobile) ═══ */}
      <section
        className="relative overflow-hidden grain border-b border-[#DCB457]/15 pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-10 lg:px-14"
        data-testid="livre-or-hero"
      >
        <div className="spotlight bg-[#DCB457]/20 w-[520px] h-[520px] -top-32 -left-24" />
        <div className="relative z-10 max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Colonne gauche : texte + CTAs */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="gold-line" />
              <span className="eyebrow">Livre d'Or</span>
            </div>
            <h1 className="font-serif-luxe text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.25rem] leading-[0.98] tracking-tight">
              Le Livre d'Or qui fait{" "}
              <span className="italic gold-text">revivre vos souvenirs.</span>
            </h1>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
              Audio ou audio &amp; vidéo, laissez vos invités vous transmettre
              leurs messages, leurs rires et leurs souvenirs les plus précieux.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#formules"
                data-testid="livre-or-cta-formules"
                className="btn-gold"
              >
                Découvrir les formules{" "}
                <ArrowRight size={16} strokeWidth={1.4} />
              </a>
              <Link
                to="/contact"
                data-testid="livre-or-cta-devis-top"
                className="btn-ghost-gold"
              >
                Demander un devis
              </Link>
            </div>
          </div>
          {/* Colonne droite : photo produit (scénographie Livre d'Or) */}
          <div className="relative">
            <div
              className="relative aspect-[4/5] md:aspect-[5/6] card-luxe !p-0 overflow-hidden bg-black"
              data-testid="livre-or-hero-image"
            >
              <img
                src={HERO_IMG}
                alt="Borne Livre d'Or My Animation — scénographie mariage avec téléphone rétro et fleurs"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>
            <p
              className="text-white/45 text-[11px] italic mt-3 text-right"
              data-testid="livre-or-photo-notice"
            >
              Photo non contractuelle — modèle selon disponibilité.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ Section : Deux expériences ═══ */}
      <section
        id="formules"
        className="scroll-mt-24 py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15"
        data-testid="livre-or-formules-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal text-center">
            <span className="eyebrow">Choisissez votre expérience</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Deux formules, un même{" "}
              <span className="italic gold-text">souvenir précieux.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Carte AUDIO */}
            <div
              className="card-luxe !p-0 reveal relative flex flex-col overflow-hidden"
              data-testid="livre-or-card-audio"
            >
              <div className="relative aspect-[16/10] border-b border-[#DCB457]/15 bg-white">
                <img
                  src={AUDIO_IMG}
                  alt="Livre d'Or Audio — téléphone rétro blanc pour enregistrer les messages vocaux"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain p-6"
                />
              </div>
              <div className="p-7 md:p-9 flex flex-col flex-1">
              <Mic size={28} strokeWidth={1} className="text-[#DCB457] mb-4" />
              <h3 className="font-serif-luxe text-[24px] md:text-[28px] leading-[1.12] tracking-[-0.005em] pb-3 border-b border-[#DCB457]/15">
                Livre d'Or Audio
              </h3>
              <p className="text-white/60 text-[14px] mt-3 leading-relaxed">
                Un téléphone vintage ou dispositif audio dédié permet à vos
                invités d'enregistrer leurs messages vocaux tout au long de
                votre événement.
              </p>
              <div className="mt-5 border-t border-[#DCB457]/20 pt-5">
                <span
                  className="font-serif-luxe text-4xl md:text-5xl gold-text"
                  data-testid="livre-or-price-audio"
                >
                  {price("Livre d'Or Audio")}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-white/45 ml-2">
                  TTC
                </span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {AUDIO_POINTS.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-white/75 text-[14px] leading-snug"
                  >
                    <Check
                      size={15}
                      strokeWidth={1.5}
                      className="text-[#DCB457] mt-0.5 shrink-0"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="livre-or-cta-audio"
                className="btn-gold mt-8 w-full !py-3 !text-[11px]"
              >
                Choisir cette formule
              </Link>
              </div>
            </div>

            {/* Carte AUDIO & VIDÉO */}
            <div
              className="card-luxe !p-0 reveal relative flex flex-col overflow-hidden border-[#DCB457]/60"
              data-testid="livre-or-card-video"
            >
              <div className="relative aspect-[16/10] border-b border-[#DCB457]/15 bg-white">
                <img
                  src={VIDEO_IMG}
                  alt="Livre d'Or Audio & Vidéo — cabine rétro avec écran vidéo pour capturer les messages en image"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-contain p-6"
                />
                <span className="absolute top-4 left-4 bg-[#DCB457] text-black text-[10px] uppercase tracking-[0.22em] font-medium px-3 py-1 flex items-center gap-1.5 z-20">
                  <Sparkles size={11} strokeWidth={2} /> Expérience Premium
                </span>
              </div>
              <div className="p-7 md:p-9 flex flex-col flex-1">
              <Video
                size={28}
                strokeWidth={1}
                className="text-[#DCB457] mb-4"
              />
              <h3 className="font-serif-luxe text-[24px] md:text-[28px] leading-[1.12] tracking-[-0.005em] pb-3 border-b border-[#DCB457]/15">
                Livre d'Or Audio &amp; Vidéo
              </h3>
              <p className="text-white/60 text-[14px] mt-3 leading-relaxed">
                L'expérience ultime pour conserver non seulement la voix, mais
                aussi l'émotion et les expressions de vos proches grâce à la
                vidéo.
              </p>
              <div className="mt-5 border-t border-[#DCB457]/20 pt-5">
                <span
                  className="font-serif-luxe text-4xl md:text-5xl gold-text"
                  data-testid="livre-or-price-video"
                >
                  {price("Livre d'Or Audio & Vidéo")}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-white/45 ml-2">
                  TTC
                </span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {VIDEO_POINTS.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-white/75 text-[14px] leading-snug"
                  >
                    <Check
                      size={15}
                      strokeWidth={1.5}
                      className="text-[#DCB457] mt-0.5 shrink-0"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="livre-or-cta-video"
                className="btn-gold mt-8 w-full !py-3 !text-[11px]"
              >
                Choisir cette formule
              </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-white/45 text-xs mt-8 uppercase tracking-[0.18em]">
            Tarifs TTC · TVA non applicable art. 293 B du CGI
          </p>
        </div>
      </section>

      {/* ═══ Section : Support (option) ═══ */}
      <section
        className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto"
        data-testid="livre-or-support-section"
      >
        <div className="mb-10 reveal text-center">
          <span className="eyebrow">Besoin d'un accompagnement ?</span>
          <h2 className="font-serif-luxe text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.08]">
            Zéro logistique, <span className="italic gold-text">tout est pris en charge.</span>
          </h2>
        </div>
        <div
          className="card-luxe p-8 md:p-10 reveal flex flex-col md:flex-row md:items-center gap-6 md:gap-10 border-l-2 border-l-[#DCB457]/50"
          data-testid="livre-or-card-support"
        >
          <div className="flex flex-col items-center md:items-start shrink-0">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#DCB457] mb-3 md:mb-4 border border-[#DCB457]/40 px-3 py-1">
              Option
            </span>
            <Truck size={44} strokeWidth={1} className="text-[#DCB457]" />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h3 className="font-serif-luxe text-2xl md:text-3xl">
                Support Livre d'Or
              </h3>
              <span
                className="font-serif-luxe text-2xl md:text-3xl gold-text"
                data-testid="livre-or-price-support"
              >
                +{price("Support Livre d'Or")}
              </span>
            </div>
            <p className="text-white/65 mt-3 leading-relaxed">
              Nous nous occupons de la livraison, de l'installation et de la
              reprise du matériel afin que vous puissiez profiter pleinement de
              votre événement.
            </p>
            <p className="text-[12px] uppercase tracking-[0.2em] text-[#DCB457] mt-4">
              Disponible en option avec votre Livre d'Or.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="livre-or-cta-support"
            className="btn-ghost-gold shrink-0"
          >
            Ajouter cette option
          </Link>
        </div>
      </section>

      {/* ═══ Section éditoriale : émotion ═══ */}
      <section
        className="py-24 md:py-28 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15"
        data-testid="livre-or-emotion-section"
      >
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div className="reveal">
            <span className="eyebrow">Une émotion, préservée pour toujours</span>
            <h2 className="font-serif-luxe text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.08]">
              Des messages que vous pourrez{" "}
              <span className="italic gold-text">réécouter encore et encore.</span>
            </h2>
            <div className="mt-6 space-y-4 text-white/70 leading-relaxed text-[15px]">
              <p>
                Un mariage passe en quelques heures. Les souvenirs, eux,
                restent.
              </p>
              <p>
                Vos proches pourront laisser un message à leur manière :
                quelques mots tendres, une anecdote, un fou rire ou simplement
                une déclaration spontanée.
              </p>
              <p>
                Des années plus tard, vous pourrez retrouver ces voix et
                revivre ces instants comme si vous y étiez.
              </p>
            </div>
          </div>
          <div className="reveal relative aspect-[4/5] card-luxe !p-0 overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80"
              alt="Mariés partageant un moment d'émotion — invités laissant un message d'amour"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <p className="absolute bottom-4 left-4 right-4 text-white/50 text-[10px] italic">
              Photo non contractuelle — mise en situation.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ Section : Comment ça marche ═══ */}
      <section
        className="py-24 md:py-28 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto"
        data-testid="livre-or-how-section"
      >
        <div className="mb-14 reveal text-center">
          <span className="eyebrow">Comment ça marche</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Simple. Fluide.{" "}
            <span className="italic gold-text">Inoubliable.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {HOW_STEPS.map((s) => (
            <div
              key={s.n}
              className="card-luxe p-7 md:p-8 reveal"
              data-testid={`livre-or-step-${s.n}`}
            >
              <span className="font-serif-luxe text-5xl md:text-6xl gold-text block leading-none">
                {s.n}
              </span>
              <h3 className="font-serif-luxe text-xl md:text-2xl mt-5">
                {s.title}
              </h3>
              <p className="text-white/60 mt-3 text-[14px] leading-relaxed">
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ Section : Comparatif ═══ */}
      <section
        className="py-16 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1200px] mx-auto"
        data-testid="livre-or-compare-section"
      >
        <div className="mb-10 reveal text-center">
          <span className="eyebrow">Tableau comparatif</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">
            Comparez nos <span className="italic gold-text">formules.</span>
          </h2>
        </div>
        <div
          className="card-luxe !p-0 overflow-x-auto reveal"
          data-testid="livre-or-compare-wrapper"
        >
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr className="border-b border-[#DCB457]/25">
                <th className="font-serif-luxe text-base md:text-lg text-white/70 px-5 md:px-8 py-5">
                  &nbsp;
                </th>
                <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5 text-center">
                  Audio
                </th>
                <th className="font-serif-luxe text-base md:text-lg text-[#DCB457] px-5 md:px-8 py-5 text-center">
                  Audio &amp; Vidéo
                </th>
              </tr>
            </thead>
            <tbody className="text-[14px] md:text-[15px]">
              <tr className="border-b border-white/5">
                <td className="px-5 md:px-8 py-4 text-white/80">
                  Messages vocaux
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="px-5 md:px-8 py-4 text-white/80">
                  Souvenirs audio
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="px-5 md:px-8 py-4 text-white/80">
                  Souvenirs vidéo
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-white/30">
                  —
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
              </tr>
              <tr>
                <td className="px-5 md:px-8 py-4 text-white/80">
                  Expérience premium
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-white/30">
                  —
                </td>
                <td className="px-5 md:px-8 py-4 text-center text-[#DCB457]">
                  ✓
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-center text-white/55 text-sm mt-6 uppercase tracking-[0.18em]">
          Support installation/livraison : +{price("Support Livre d'Or")}
        </p>
      </section>

      {/* ═══ Section : Pour quels événements ? ═══ */}
      <section
        className="py-24 md:py-28 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15"
        data-testid="livre-or-events-section"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal text-center">
            <span className="eyebrow">Pour quels événements ?</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Le Livre d'Or s'adapte à{" "}
              <span className="italic gold-text">tous vos événements.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {EVENT_TYPES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="card-luxe p-6 md:p-7 text-center reveal"
                data-testid={`livre-or-event-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <Icon
                  size={30}
                  strokeWidth={1}
                  className="text-[#DCB457] mx-auto mb-4"
                />
                <p className="font-serif-luxe text-lg md:text-xl">{label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-white/60 text-base md:text-lg mt-10 max-w-2xl mx-auto leading-relaxed italic">
            Parce que chaque événement mérite de conserver une trace de ceux
            qui l'ont partagé avec vous.
          </p>
        </div>
      </section>

      {/* ═══ Section : FAQ ═══ */}
      <section
        className="py-20 md:py-24 px-6 md:px-10 lg:px-14 max-w-[1000px] mx-auto"
        data-testid="livre-or-faq-section"
      >
        <div className="text-center mb-12 reveal">
          <span className="eyebrow">FAQ Livre d'Or</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.05]">
            Vos questions sur{" "}
            <span className="italic gold-text">le Livre d'Or.</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          data-testid="livre-or-faq-accordion"
        >
          {LIVRE_OR_FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`lo-faq-${i}`}
              data-testid={`livre-or-faq-item-${i}`}
              className="card-luxe !rounded-none px-6 md:px-8"
            >
              <AccordionTrigger className="font-serif-luxe text-lg md:text-xl text-left hover:no-underline data-[state=open]:text-[#DCB457] py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ═══ CTA Bottom ═══ */}
      <section className="py-16 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <div className="card-luxe p-8 md:p-12 reveal text-center">
          <span className="eyebrow">Prêt à créer des souvenirs inoubliables ?</span>
          <h2 className="font-serif-luxe text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.08]">
            Réservez votre Livre d'Or pour{" "}
            <span className="italic gold-text">votre grand jour.</span>
          </h2>
          <div className="flex justify-center mt-8">
            <Link
              to="/contact"
              data-testid="livre-or-cta-final-devis"
              className="btn-gold"
            >
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
          </div>
        </div>
      </section>

      <NearbyCitiesLinks
        eyebrow="Livre d'Or près de chez vous"
        title={
          <>
            Livre d'Or <span className="italic gold-text">dans votre ville.</span>
          </>
        }
        urlPrefix="dj-mariage"
        intro="My Animation propose son Livre d'Or Audio et Audio & Vidéo pour les mariages, anniversaires et événements privés dans tout le Nord, le Pas-de-Calais, les Hauts-de-France et en Belgique frontalière."
        citySlugs={[
          "lille",
          "tourcoing",
          "roubaix",
          "armentieres",
          "dunkerque",
          "lens",
          "arras",
          "valenciennes",
          "cambrai",
          "bethune",
        ]}
        testidPrefix="livre-or-nearby"
      />

      <CTAFooter />
    </div>
  );
}
